import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";
import type {
  AdviceStatusRequest,
  ApplyDeskImprovementsRequest,
  ApplyDeskImprovementsResponse,
  DeskImprovementRun,
  DeskImprovementRunListResponse,
  DeskImprovementRunSummary,
  DeskImprovementSuggestion,
  StartDeskImprovementRunRequest,
} from "@/lib/types/desk-improvements";

export const IMPROVEMENTS_PATH =
  "/proxy/desk-designer/api/v1/internal/desk-improvements";

export const deskImprovementsQueryKey = [
  "desk-designer",
  "desk-improvements",
] as const;

/** Runs take minutes; they are polled until they settle. */
const IN_PROGRESS_POLL_MS = 5000;

function isInProgress(run: DeskImprovementRunSummary): boolean {
  return run.status === "pending" || run.status === "running";
}

export function useDeskImprovementRuns(tenantId: string) {
  return useQuery({
    queryKey: [...deskImprovementsQueryKey, tenantId],
    queryFn: async () => {
      const { data } =
        await backofficeClient.get<DeskImprovementRunListResponse>(
          IMPROVEMENTS_PATH,
          { params: { tenant_id: tenantId } },
        );
      return data.items;
    },
    enabled: Boolean(tenantId),
    refetchInterval: (query) =>
      query.state.data?.some(isInProgress) ? IN_PROGRESS_POLL_MS : false,
  });
}

export function useDeskImprovementRun(tenantId: string, runId: string) {
  return useQuery({
    queryKey: [...deskImprovementsQueryKey, tenantId, runId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<DeskImprovementRun>(
        `${IMPROVEMENTS_PATH}/${runId}`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    enabled: Boolean(tenantId) && Boolean(runId),
    refetchInterval: (query) =>
      query.state.data && isInProgress(query.state.data)
        ? IN_PROGRESS_POLL_MS
        : false,
  });
}

export const DEFAULT_WINDOW_DAYS = 180;

interface StartInput {
  tenantId: string;
  windowDays: number;
}

export function useStartDeskImprovementRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, windowDays }: StartInput) => {
      const body: StartDeskImprovementRunRequest | undefined =
        windowDays === DEFAULT_WINDOW_DAYS
          ? undefined
          : { window_days: windowDays };
      const { data } = await backofficeClient.post<DeskImprovementRunSummary>(
        IMPROVEMENTS_PATH,
        body,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    onSettled: async (_data, _error, { tenantId }) => {
      await queryClient.invalidateQueries({
        queryKey: [...deskImprovementsQueryKey, tenantId],
      });
    },
  });
}

interface ApplyInput {
  tenantId: string;
  runId: string;
  suggestionIds: string[];
}

/** Writes the chosen suggestions to the tenant's desks; one outcome each. */
export function useApplyDeskImprovements() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, runId, suggestionIds }: ApplyInput) => {
      const body: ApplyDeskImprovementsRequest = {
        suggestion_ids: suggestionIds,
      };
      const { data } =
        await backofficeClient.post<ApplyDeskImprovementsResponse>(
          `${IMPROVEMENTS_PATH}/${runId}/apply`,
          body,
          { params: { tenant_id: tenantId } },
        );
      return data;
    },
    onSuccess: (data, { tenantId, runId }) => {
      queryClient.setQueryData(
        [...deskImprovementsQueryKey, tenantId, runId],
        data.run,
      );
    },
    onSettled: async (_data, _error, { tenantId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...deskImprovementsQueryKey, tenantId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["service-desk", "desks", tenantId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["service-desk", "subdesks", tenantId],
        }),
      ]);
    },
  });
}

interface AdviceStatusInput {
  tenantId: string;
  runId: string;
  suggestionId: string;
  status: AdviceStatusRequest;
}

/** Marks configuration advice done, rejected or back to pending. */
export function useSetAdviceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      runId,
      suggestionId,
      status,
    }: AdviceStatusInput) => {
      const { data } = await backofficeClient.put<DeskImprovementSuggestion>(
        `${IMPROVEMENTS_PATH}/${runId}/suggestions/${suggestionId}/status`,
        { status },
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    onSuccess: (suggestion, { tenantId, runId }) => {
      queryClient.setQueryData<DeskImprovementRun>(
        [...deskImprovementsQueryKey, tenantId, runId],
        (run) =>
          run && {
            ...run,
            suggestions: run.suggestions.map((s) =>
              s.id === suggestion.id ? suggestion : s,
            ),
          },
      );
    },
  });
}
